import { lazy, Suspense, useState } from "react";
import { Button, Modal } from "@shop-ledger/ui-components";

const CameraScanner = lazy(() =>
  import("../hardware/scanner/CameraScanner").then((m) => ({ default: m.CameraScanner })),
);

export interface BarcodeScanButtonProps {
  onScan: (code: string) => void;
  label?: string;
}

/**
 * A "Scan barcode" trigger for back-office forms (business-owner walkthrough follow-up:
 * scanning to fill in a new product's SKU, and scanning to find an item when adding
 * stock) — the same camera-scan capability the till already offered (system_architecture.md
 * §4.2), just not previously reachable outside `/pos/*`.
 *
 * `CameraScanner` is lazy-loaded here (not imported statically) so `html5-qrcode`
 * (~800KB) only downloads for a session that actually opens the scanner, rather than
 * landing in the entry chunk every back-office page pays for — the same reasoning
 * `App.tsx` documents for why the whole `/pos/*` subtree is a lazy route.
 */
export function BarcodeScanButton({ onScan, label = "Scan barcode" }: BarcodeScanButtonProps) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button type="button" variant="secondary" onClick={() => setOpen(true)}>
        {label}
      </Button>
      <Modal open={open} onClose={() => setOpen(false)} title="Camera scan">
        <Suspense fallback={<p className="muted">Loading camera…</p>}>
          <CameraScanner
            onScan={(code) => {
              setOpen(false);
              onScan(code);
            }}
            onClose={() => setOpen(false)}
          />
        </Suspense>
      </Modal>
    </>
  );
}
